/* eslint-disable no-underscore-dangle */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { ChevronLeft, HardDriveUpload } from 'lucide-react'
import { useRouter } from 'next/router'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import * as yup from 'yup'
import { trackOffer } from '@/utils/algolia/insights'
import { sendEvent } from '@/utils/gtag'
import { trackMxEvent } from '@/utils/mixpanel'
import { parseAmountInput } from '@/utils/string'
import { generateOfferDescription } from '@/data/api/ai'
import { createOffer } from '@/data/api/offer'
import useAuth from '@/hooks/useAuth'
import useUser from '@/hooks/useUser'
import Avatar from '@/components/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FileUploader } from '@/components/ui/uploader'
import { ProfessionalModal } from '../modal'
import { DecidedForm } from './decided-form'
import QuestionAnswerFields from './question-answer-fields'
import {
  commonSchema,
  decidedSchema,
  undecidedSchema,
} from './schemas'
import ServiceSelect, { OTHER_SERVICE_VALUE } from './service-select'
import { UndecidedForm } from './undecided-form'
import type {
  CommonSchema,
  DecidedSchema,
  UndecidedSchema,
} from './schemas'
import type { TOffer, TOfferAnswer, TTemplate } from '@/interfaces/offer'
import type { TProCategory } from '@/interfaces/user'
import { TR2Type } from '@/interfaces'
import { DEADLINE_OPTION, TDeadline } from '@/interfaces/offer'
import { TServiceQuestionType } from '@/interfaces/service-question'
import { cn } from '@/lib/utils'
import { yupResolver } from '@/lib/yup-resolver'

type Props = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  username: string
  category?: TProCategory
}

type SubmitValues = {
  title: string
  description: string
  deadline: TDeadline
  budget?: number | null
  _files?: { url: string; name: string }[]
  _serviceCategoryId?: string
  answers?: TOfferAnswer[]
}

export default function OfferModal({
  isOpen,
  onOpenChange,
  username,
  category,
}: Props) {
  const { currentUser } = useAuth()
  const { user } = useUser({ key: 'username', value: username })
  const router = useRouter()
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ isGenerating, setIsGenerating ] = useState(false)
  const [ selectedTemplate, setSelectedTemplate ] = useState<TTemplate | null>(null)
  const [ activeTab, setActiveTab ] = useState<'undecided' | 'decided'>('undecided')
  const [ currentStep, setCurrentStep ] = useState<'main' | 'questions'>('main')
  const [ noBudget, setNoBudget ] = useState(false)
  // 依頼者が予算欄を自分で編集したか。サービスの参考価格で上書きしてよいかの判定に使う
  const isBudgetEditedRef = useRef(false)

  const categories = useMemo(() => user?.categories || [], [ user?.categories ])

  const initialServiceId = useMemo(() => {
    if (category?.id && categories.some((c) => c.id === category.id)) return category.id
    return ''
  }, [ category?.id, categories ])

  const commonValidationSchema = useMemo(() => commonSchema.shape({
    answers: yup
      .array()
      .of(yup.object().shape({
        questionId: yup.string().required(),
        label: yup.string().required(),
        type: yup
          .mixed<TServiceQuestionType>()
          .oneOf(Object.values(TServiceQuestionType))
          .required(),
        value: yup.string().optional(),
        files: yup
          .array()
          .of(yup.object().shape({
            url: yup.string().required(),
            name: yup.string().required(),
          }))
          .optional(),
      })
        .test('required-by-question', 'この項目は必須です', function (answer) {
          const ctxCategories = (
            this.options.context as { categories?: TProCategory[] } | undefined
          )?.categories || []
          const ctxSelectedId = (
            this.options.context as { serviceCategoryId?: string } | undefined
          )?.serviceCategoryId
          const selected = ctxCategories.find((c) => c.id === ctxSelectedId)
          const question = selected?.questions?.find((q) => q.id === answer?.questionId)
          if (!question?.required) return true
          if (question.type === TServiceQuestionType.FILE) {
            if (!answer?.files?.length) {
              return this.createError({ path: `${this.path}.files`, message: 'この項目は必須です' })
            }
            return true
          }
          if (!answer?.value?.toString().trim()) {
            return this.createError({ path: `${this.path}.value`, message: 'この項目は必須です' })
          }
          return true
        }))
      .optional(),
  }), [])

  const commonForm = useForm<CommonSchema>({
    defaultValues: {
      _serviceCategoryId: initialServiceId,
      deadline: TDeadline.UNDECIDED,
      budget: null,
      _files: [],
      answers: [],
    },
    resolver: yupResolver(commonValidationSchema),
  })

  const decidedForm = useForm<DecidedSchema>({
    defaultValues: {
      title: category?.serviceTitle || category?.title || '',
      description: '',
    },
    resolver: yupResolver(decidedSchema),
  })

  const undecidedForm = useForm<UndecidedSchema>({
    defaultValues: {
      keyword: '',
      title: '',
      description: '',
    },
    resolver: yupResolver(undecidedSchema),
  })

  const watchedServiceCategoryId = useWatch({
    control: commonForm.control,
    name: '_serviceCategoryId',
  })

  const selectedCategory = useMemo(() => {
    if (!watchedServiceCategoryId || watchedServiceCategoryId === OTHER_SERVICE_VALUE) {
      return undefined
    }
    return categories.find((c) => c.id === watchedServiceCategoryId)
  }, [ watchedServiceCategoryId, categories ])

  const decidedQuestions = selectedCategory?.questions || []

  // モーダルを開いたとき、category prop に対応するサービスがあれば自動入力
  useEffect(() => {
    if (!isOpen) return
    if (!initialServiceId) return
    if (commonForm.getValues('_serviceCategoryId')) return
    commonForm.setValue('_serviceCategoryId', initialServiceId)
  }, [ isOpen, initialServiceId, commonForm ])

  // サービス変更時に answers エラーをクリア
  useEffect(() => {
    commonForm.clearErrors('answers')
  }, [ watchedServiceCategoryId, commonForm ])

  // 選択カテゴリ変化時に answers を初期化し、title をサービス名で反映
  useEffect(() => {
    if (!selectedCategory) {
      commonForm.setValue('answers', [])
      return
    }
    commonForm.setValue(
      'answers',
      (selectedCategory.questions || []).map((q) => ({
        questionId: q.id,
        label: q.label,
        type: q.type,
        value: '',
        files: [],
      })),
    )
    decidedForm.setValue(
      'title',
      selectedCategory.serviceTitle || selectedCategory.title || '',
      { shouldValidate: true },
    )
    // 参考価格の自動入力はあくまで未入力のときの初期値。依頼者が自分で入れた予算は
    // サービスを選び直しても消さない（参考価格が無いサービスだと黙って空になってしまう）
    if (!noBudget && !isBudgetEditedRef.current) {
      commonForm.setValue('budget', selectedCategory.price ?? null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedCategory?.id ])

  const submitOffer = useCallback(async (values: SubmitValues) => {
    if (!user?.id) return
    setIsSubmitting(true)
    const isOther = values._serviceCategoryId === OTHER_SERVICE_VALUE
    const result = await createOffer({
      title: values.title,
      description: values.description,
      deadline: values.deadline,
      budget: noBudget ? null : values.budget,
      _files: values._files,
      _serviceCategoryId: isOther ? undefined : values._serviceCategoryId,
      answers: values.answers,
      professionalId: user.id,
    } as Partial<TOffer> & { professionalId: string })
    if ('error' in result) {
      toast.error(result.error || 'エラーが発生しました')
      setIsSubmitting(false)
      return
    }

    trackOffer({
      userId: currentUser?.id,
      offeredUserId: user.id,
      serviceId: isOther ? undefined : (values._serviceCategoryId || category?.id),
    })

    sendEvent({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'JPY',
        items: [
          {
            item_id: result.projectId,
            item_name: values.title,
            quantity: 1,
          },
        ],
      },
    })

    const fileCount = values._files ? values._files.length : 0
    trackMxEvent('Pro Contact Submit', {
      username: user.username,
      name: user.name,
      status: user.offerStatus,
      title: values.title,
      limit: values.deadline,
      files: fileCount,
      service_id: isOther ? 'OTHER' : (values._serviceCategoryId || category?.id),
    })
    onOpenChange(false)
    router.push(`/project/${result.projectId}`)
  }, [ currentUser?.id, category?.id, noBudget, onOpenChange, router, user ])

  const onSubmitDecided = useCallback((
    values: DecidedSchema,
    commonValues: CommonSchema,
  ) => {
    return submitOffer({
      title: values.title,
      description: values.description,
      deadline: commonValues.deadline,
      budget: commonValues.budget,
      _files: commonValues._files,
      _serviceCategoryId: commonValues._serviceCategoryId,
      answers: (commonValues.answers || []) as TOfferAnswer[],
    })
  }, [ submitOffer ])

  const onSubmitUndecided = useCallback((
    values: UndecidedSchema,
    commonValues: CommonSchema,
  ) => {
    return submitOffer({
      title: values.title,
      description: values.description,
      deadline: commonValues.deadline,
      budget: commonValues.budget,
      _files: commonValues._files,
      _serviceCategoryId: commonValues._serviceCategoryId,
      answers: (commonValues.answers || []) as TOfferAnswer[],
    })
  }, [ submitOffer ])

  const handleGenerateDescription = useCallback(async () => {
    const keyword = undecidedForm.getValues('keyword')
    if (!keyword || keyword.length < 5) {
      toast.error('依頼内容を5文字以上で入力してください')
      return
    }
    if (!user?.id) return

    const categoryId = selectedCategory?.id || category?.id
    const deadline = commonForm.getValues('deadline')

    setIsGenerating(true)
    const result = await generateOfferDescription({
      keyword,
      professionalId: user.id,
      categoryId,
      deadline,
    })
    setIsGenerating(false)

    if ('error' in result) {
      toast.error('文章の生成に失敗しました。もう一度お試しください。')
      return
    }

    undecidedForm.setValue('title', result.title, { shouldValidate: true })
    undecidedForm.setValue('description', result.description, { shouldValidate: true })
  }, [ undecidedForm, user, category, selectedCategory, commonForm ])

  const hasQuestions = decidedQuestions.length > 0

  const applyValidationErrors = useCallback((e: unknown) => {
    if (e && typeof e === 'object' && 'inner' in e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const err of (e as any).inner || []) {
        if (err.path) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          commonForm.setError(err.path as any, { type: 'manual', message: err.message })
        }
      }
    }
  }, [ commonForm ])

  const handleModalSubmit = useCallback(async () => {
    const commonValues = commonForm.getValues()

    if (currentStep === 'main') {
      // メイン: サービス/納期/タブ内のフォームのみ検証 (answers は次ステップで検証)
      try {
        await commonSchema.validate(commonValues, { abortEarly: false })
        commonForm.clearErrors('_serviceCategoryId')
        commonForm.clearErrors('deadline')
        commonForm.clearErrors('_files')
      }
      catch (e) {
        applyValidationErrors(e)
        return
      }

      const isTabValid = activeTab === 'undecided'
        ? await undecidedForm.trigger()
        : await decidedForm.trigger()
      if (!isTabValid) return

      if (hasQuestions) {
        setCurrentStep('questions')
        return
      }
    }
    else {
      // 質問ステップ: answers の動的バリデーションを実行
      try {
        await commonValidationSchema.validate(commonValues, {
          abortEarly: false,
          context: {
            categories,
            serviceCategoryId: commonValues._serviceCategoryId,
          },
        })
        commonForm.clearErrors()
      }
      catch (e) {
        applyValidationErrors(e)
        return
      }
    }

    if (activeTab === 'undecided') {
      await undecidedForm.handleSubmit((values) => onSubmitUndecided(values, commonValues))()
    }
    else {
      await decidedForm.handleSubmit((values) => onSubmitDecided(values, commonValues))()
    }
  }, [
    currentStep, hasQuestions, activeTab, commonForm,
    commonValidationSchema, categories, applyValidationErrors,
    undecidedForm, decidedForm, onSubmitUndecided, onSubmitDecided,
  ])

  useEffect(() => {
    if (!isOpen) {
      commonForm.reset({
        _serviceCategoryId: initialServiceId,
        deadline: TDeadline.UNDECIDED,
        budget: null,
        _files: [],
        answers: [],
      })
      decidedForm.reset({
        title: category?.serviceTitle || category?.title || '',
        description: '',
      })
      undecidedForm.reset()
      setIsSubmitting(false)
      setActiveTab('undecided')
      setSelectedTemplate(null)
      setCurrentStep('main')
      setNoBudget(false)
      isBudgetEditedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isOpen ])

  return (
    <ProfessionalModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="お問い合わせ・相談"
      isEdit
      onSubmit={handleModalSubmit}
      isSubmitting={isSubmitting || isGenerating}
      submitLabel={currentStep === 'main' && hasQuestions ? '次へ' : '送信'}
    >
      <Form {...commonForm}>
        {currentStep === 'questions' ? (
          <div className="space-y-6">
            <button
              type="button"
              onClick={() => setCurrentStep('main')}
              className="inline-flex items-center gap-1 text-sm text-ghost hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
              戻る
            </button>
            <div>
              <h2 className="mb-1 text-lg font-bold">クリエイターからの質問</h2>
              <p className="mb-0 text-sm text-ghost">
                依頼するサービスについて、いくつか質問にお答えください。
              </p>
            </div>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
              <QuestionAnswerFields questions={decidedQuestions} />
            </form>
          </div>
        ) : (
          <>
            <div className="flex gap-4 rounded-lg border border-border p-4">
              <div className="shrink-0">
                <Avatar username={username} className="h-12 w-12" />
              </div>
              <div>
                <h2 className="text-lg font-bold">{user?.name}</h2>
                <p className="mb-0 text-sm text-ghost">{user?.title}</p>
              </div>
            </div>
            <p className="mb-0 text-sm text-ghost">
              クリエイターにお問い合わせや気になっていることを相談してみましょう。
              <br />
              送信後、クリエイターと直接メッセージのやりとりができるようになります。
            </p>

            <form
              onSubmit={(e) => e.preventDefault()}
              className="space-y-8 pt-6"
            >
              <ServiceSelect categories={categories} />
              <FormField
                control={commonForm.control}
                name="budget"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>予算</FormLabel>
                    <div className="flex items-center gap-4">
                      <div className="relative w-40">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ghost">¥</span>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const parsed = parseAmountInput(e.target.value)
                              // 空にしたら「自分で入れた予算」は無くなったので、
                              // サービスを選び直したときに参考価格の自動入力へ戻す
                              isBudgetEditedRef.current = parsed !== null
                              field.onChange(parsed)
                            }}
                            className="pl-7"
                            type="text"
                            inputMode="numeric"
                            placeholder={selectedCategory?.price != null ? String(selectedCategory.price) : '50000'}
                            disabled={noBudget}
                          />
                        </FormControl>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="noBudget"
                          checked={noBudget}
                          onCheckedChange={(checked) => {
                            setNoBudget(!!checked)
                            // 入力値は破棄されるので、以降は参考価格の自動入力に戻す
                            isBudgetEditedRef.current = false
                            if (checked) {
                              commonForm.setValue('budget', null)
                            }
                            else {
                              commonForm.setValue('budget', selectedCategory?.price ?? null)
                            }
                          }}
                        />
                        <label htmlFor="noBudget" className="mb-0 cursor-pointer text-sm font-medium">
                          予算は決まっていない
                        </label>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={commonForm.control}
                name="deadline"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>希望納期</FormLabel>
                    <div className="max-w-42">
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="選択してください" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={TDeadline.WITHIN_TWO_WEEKS}>
                            {DEADLINE_OPTION[TDeadline.WITHIN_TWO_WEEKS]}
                          </SelectItem>
                          <SelectItem value={TDeadline.WITHIN_ONE_MONTH}>
                            {DEADLINE_OPTION[TDeadline.WITHIN_ONE_MONTH]}
                          </SelectItem>
                          <SelectItem value={TDeadline.UNDECIDED}>
                            {DEADLINE_OPTION[TDeadline.UNDECIDED]}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>

            <div className="pt-6">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setActiveTab('undecided')}
                  className={cn(
                    'rounded-lg py-4 text-base font-semibold transition-colors',
                    activeTab === 'undecided'
                      ? 'bg-primary text-white'
                      : 'bg-primary/10 text-foreground hover:bg-primary/20',
                  )}
                >
                  AI で依頼内容を生成
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('decided')}
                  className={cn(
                    'rounded-lg py-4 text-base font-semibold transition-colors',
                    activeTab === 'decided'
                      ? 'bg-primary text-white'
                      : 'bg-primary/10 text-foreground hover:bg-primary/20',
                  )}
                >
                  依頼内容が決まっている
                </button>
              </div>

              <div className="py-6">
                {activeTab === 'undecided' ? (
                  <UndecidedForm
                    form={undecidedForm}
                    onSubmit={(values) => onSubmitUndecided(values, commonForm.getValues())}
                    onGenerate={handleGenerateDescription}
                    isGenerating={isGenerating}
                    isSubmitting={isSubmitting}
                  />
                ) : (
                  <DecidedForm
                    form={decidedForm}
                    onSubmit={(values) => onSubmitDecided(values, commonForm.getValues())}
                    selectedTemplate={selectedTemplate}
                    onTemplateChange={setSelectedTemplate}
                  />
                )}
              </div>
            </div>

            <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
              <FormField
                control={commonForm.control}
                name="_files"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>添付ファイル</FormLabel>
                    <FormDescription>
                      ミックスの仕上がりイメージや、参考音源があれば添付してください。
                    </FormDescription>
                    <FormControl>
                      <FileUploader
                        className="max-w-70 rounded-lg"
                        onChange={field.onChange}
                        type={TR2Type.ProjectSound}
                      >
                        <div className="flex cursor-pointer items-center gap-2 rounded-lg border border-border py-3 px-4 text-sm hover:border-primary/50">
                          <span className="rounded-full bg-ghost-light p-2">
                            <HardDriveUpload className="h-3 w-3" />
                          </span>
                          <span className="text-ghost">ファイルをアップロード</span>
                        </div>
                      </FileUploader>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </>
        )}
      </Form>
    </ProfessionalModal>
  )
}
